import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { colorGray, token } from '@atlas-frontend/design-tokens';
import { Segmented, SegmentedProps } from 'antd';
import styled from 'styled-components';
import { useGetIdFromName } from '../../form';

export interface CustomSegmentedOptionObject<Segment extends string = string> {
  className?: string;
  disabled?: boolean;
  id?: string;
  value: Segment;
  label: ReactNode;
  count?: number;
}

export interface CustomSegmentedProps<Value extends string = string>
  extends Omit<SegmentedProps, 'onChange'> {
  value: Value;
  onChange?: (value: Value) => void;
  options: Array<CustomSegmentedOptionObject<Value>>;
}

export const CustomSegmented = <Value extends string = string>(
  props: CustomSegmentedProps<Value>
) => {
  const { value, onChange, options, ...rest } = props;

  const segmentOptions = useMemo(
    () =>
      options.map(({ value, ...rest }) => ({
        ...rest,
        value: value.toString(),
      })),
    [value, options]
  );

  const handleSegmentChange = useCallback(
    (value: any) => onChange?.(value),
    [onChange]
  );

  const ref = useRef<HTMLDivElement>(null);
  const getIdFromName = useGetIdFromName();

  useEffect(() => {
    const element = ref.current;
    if (element) {
      Array.from(element.getElementsByTagName('label')).forEach(
        (label, index) => {
          label.setAttribute(
            'id',
            `segment-${getIdFromName(options[index].value)}`
          );
        }
      );
    }
  }, [options]);

  return (
    <StyledSegmented
      ref={ref}
      options={segmentOptions}
      value={value}
      onChange={handleSegmentChange}
      {...(rest as any)}
    />
  );
};

const StyledSegmented = styled(Segmented)`
  background-color: ${colorGray[1]};

  .ant-segmented-item-selected {
    color: ${token.colorLink};
  }
`;
